
import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import { State } from "vuex-class"
import { CaseColumn } from "@/types/caseColumns"

import vSelect from "vue-select"
import { DataType, InsightCalculation, InsightCalculationTotal } from "@/types/insights"
import { cloneDeep } from "lodash"

import ListSelect from "@/components/Controls/ListSelect.vue"
import CalculationExpression from "../CalculationExpression.vue"
import Guid from "@/scripts/guid"
import { TenantFeatures } from "@/types/tenantFeatures"
import { InsightCalculationItem } from "@/types/insights/calculation"
import DefinitionTitle from "@/components/Config/Definitions/DefinitionTitle.vue"
import HelpIcon from "@/components/New/Page/PageComponents/HelpIcon.vue"
import { evaluate } from "mathjs"
import FieldExpression from "@/components/Fields/FieldExpression.vue"

enum CollationMode {
	All,
	Include,
	Exclude
}

@Component({
	components: {
		"v-select": vSelect,
		"list-select": ListSelect,
		"calculation-expression": CalculationExpression,
		"definition-title": DefinitionTitle,
		"help-icon": HelpIcon,
		"field-expression": FieldExpression
	}
})
export default class ExpressionCalculationTotal extends Vue {
	@State tenantEnabledFeatures!: { [key: string]: boolean }

	@Prop({ default: null })
	value!: InsightCalculationItem<InsightCalculationTotal>

	@Prop({ default: () => [] })
	fields!: CaseColumn[]

	@Prop({ default: () => [] })
	insightCalculations!: InsightCalculation[]

	controlId: string = ""
	collationMode = CollationMode

	expressionValid: boolean = true

	calculationTotalsColumns: {key: string, label: string}[] = [
		{
			key: "title",
			label: "Title"
		},
		{
			key: "groupBy",
			label: "Group By"
		},
		{
			key: "expression",
			label: "Expression"
		},
		{
			key: "buttons",
			label: ""
		}
	]

	get groupByCalculationFields (): any[] {
		if (!this.fields) {
			return []
		}

		const groupableDataTypes = [DataType.boolean, DataType.number, DataType.dateTime, DataType.array, DataType.arrayMultiple, DataType.userRole, DataType.auditUser]
		return this.fields.filter(f => groupableDataTypes.includes(f.fieldDataType!))
	}

	get allowCollation () {
		if (!this.value || !this.value.item || !this.value.item.groupBy) return false

		const field = this.fields.find(f => f.key === this.value.item.groupBy)
		if (!field) return false

		return field.dataType === "list" || field.dataType === "list-multiple" || field.dataType === "list-data"
	}

	private initializeExpression () {
		this.convertLegacyTotals()

		if (this.value.item.expression == null) {
			Vue.set(this.value.item, "expression", "")
		}

		this.removeUnnecessaryCalculations()
	}

	private convertLegacyTotals () {
		// early return if the there are no calculationList or subtractCalculationList values
		if ((!this.value.item.calculationList || this.value.item.calculationList.length === 0) &&
				(!this.value.item.subtractCalculationList || this.value.item.subtractCalculationList.length === 0)) {
			if (this.value.item.expression && this.value.item.expression!.includes("{value}")) {
				let newExpression = this.value.item.expression!.replace("{value}", "").trim()
				Vue.set(this.value.item, "expression", newExpression)
			}
			return
		}

		// ensure this function is not being run a second time on the same legacy total
		if (this.value.item.expression && !this.value.item.expression.includes("{value}")) {
			return
		}

		// create sub-expression to either append to the existing one or replace the existing one's '{value}' tag with
		let legacyAppend = ""
		let concatCalculationList: string[] = this.value.item.expressionCalculationList ?? []

		// append 'add' calculations if they're present
		if (this.value.item.calculationList && this.value.item.calculationList.length > 0) {
			concatCalculationList = concatCalculationList.concat(this.value.item.calculationList)
			for (let addCalc of this.value.item.calculationList) {
				legacyAppend += "{" + addCalc + "} + "
			}
			legacyAppend = legacyAppend.slice(0, -3)
		}

		// append 'subtract' calculations if they're present
		if (this.value.item.subtractCalculationList && this.value.item.subtractCalculationList.length > 0) {
			concatCalculationList = concatCalculationList.concat(this.value.item.subtractCalculationList)
			if (legacyAppend.length > 0) {
				legacyAppend += " - "
			} else {
				legacyAppend += "-"
			}

			for (let subCalc of this.value.item.subtractCalculationList) {
				legacyAppend += "{" + subCalc + "} - "
			}
			legacyAppend = legacyAppend.slice(0, -3)
		}

		// encompass the sub-expression with brackets so that it can be plugged anywhere '{value}' used to be
		if (legacyAppend.length > 0) {
			legacyAppend = "(" + legacyAppend + ")"
		}

		// take the existing expression and append / replace value with legacy values depending on the existing values
		let convertedExpression = this.value.item.expression?.trim() ?? ""

		if (convertedExpression.length > 0) {
			if (convertedExpression.includes("{value}")) {
				convertedExpression = convertedExpression.replace("{value}", legacyAppend)
			} else {
				// function is modified so this currently never happens, but retained the code
				convertedExpression = convertedExpression + " + " + legacyAppend
			}
		} else {
			convertedExpression = legacyAppend
		}

		Vue.set(this.value.item, "expression", convertedExpression)

		// remove duplicates from combined calculation list
		concatCalculationList = Array.from(new Set(concatCalculationList))

		// set new value for expression calculation list
		Vue.set(this.value.item, "expressionCalculationList", concatCalculationList)
	}

	get hasExtendedGrouping (): boolean {
		return this.extendedGroupingOptions.length > 0
	}

	get extendedGroupingOptions (): { value: string, label: string }[] {
		if (!this.value || !this.value.item.groupBy) return []

		const groupBy = this.fields.find(f => f.key === this.value.item.groupBy)
		if (!groupBy) return []

		if (groupBy.dataType === "user-role" &&
			this.tenantEnabledFeatures &&
			this.tenantEnabledFeatures[TenantFeatures.UserRoleLocation]) {
			return [
				{
					value: "user",
					label: "User"
				},
				{
					value: "location",
					label: "Location"
				}
			]
		}

		return []
	}

	private mounted () {
		this.controlId = Guid.newGuid()

		this.initializeExpression()
	}

	private insightsCalculationTotalList (groupBy: string | null): InsightCalculation[] {
		if (!groupBy && (!this.value || !this.value.item.expressionCalculationList || this.value.item.expressionCalculationList.length === 0)) {
			return this.insightCalculations.filter(c => !c.calculatePercentage)
		}

		return groupBy
			? this.insightCalculations.filter(c => c.groupBy === groupBy && !c.calculatePercentage)
			: this.insightCalculations.filter(c => !c.groupBy && !c.calculatePercentage)
	}

	showCalculationFormattingRules (calculation: InsightCalculation | null) {
		this.$emit("showCalculationFormattingRules", calculation)
	}

	private setCollationMode (calculationTotal: InsightCalculationTotal, collateMode: CollationMode) {
		switch (collateMode) {
			case CollationMode.All:
				Vue.set(calculationTotal, "collateInclude", null)
				Vue.set(calculationTotal, "collateExclude", null)
				break
			case CollationMode.Include:
				Vue.set(calculationTotal, "collateInclude", [])
				Vue.set(calculationTotal, "collateExclude", null)
				break
			case CollationMode.Exclude:
				Vue.set(calculationTotal, "collateInclude", null)
				Vue.set(calculationTotal, "collateExclude", [])
				break
		}
	}

	private getCollationMode (calculationTotal: InsightCalculationTotal): CollationMode {
		return calculationTotal.collateInclude
			? CollationMode.Include
			: calculationTotal.collateExclude
				? CollationMode.Exclude
				: CollationMode.All
	}

	private groupByField (groupBy: string) {
		return this.fields.find(f => f.key === groupBy)
	}

	private calculationAdded (calculations: string[]) {
		if (this.value && this.value.item && calculations.length > 0) {
			const groupBy = calculations
				.map(c => this.insightCalculations.findAndGet(calculation => calculation.key === c, c => c.groupBy, null))
				.distinct()

			this.value.item.groupBy = groupBy.length > 0 ? groupBy[0] : ""
		}
	}

	get calculationOptions (): any[] {
		let calculations = this.insightsCalculationTotalList(this.value.item.groupBy ?? null)
		let options: any[] = []
		calculations.forEach(cal => {
			if (cal.key && cal.label && !cal.calculatePercentage) {
				options.push({
					field: {
						id: cal.key,
						title: cal.label
					}
				})
			}
		})

		return options
	}

	@Watch("calculationOptions.length")
	private calculationOptionsLengthChanged (newValue: number) {
		this.removeWronglyGroupedCalculations()
	}

	expressionCalculationAdded (fieldContainer: any) {
		let calculationKey = fieldContainer.field.id
		this.calculationAdded([calculationKey])
		let expressionCalcList = this.value.item.expressionCalculationList!
		if (expressionCalcList.indexOf(calculationKey) === -1) {
			expressionCalcList.push(calculationKey)
			Vue.set(this.value.item, "expressionCalculationList", expressionCalcList)
		}
	}

	expressionCalculationRemoved (fieldContainer: any) {
		let calculationKey = fieldContainer.id
		if (!this.value.item.expression) {
			Vue.set(this.value.item, "expressionCalculationList", [])
		} else if ((this.value.item.expression!.split(calculationKey).length - 1) < 2) {
			if (this.value.item.expressionCalculationList!.indexOf(calculationKey) !== -1) {
				let expressionCalcList = this.value.item.expressionCalculationList!.filter(key => key !== calculationKey)
				Vue.set(this.value.item, "expressionCalculationList", expressionCalcList)
			}
		}
	}

	private removeUnnecessaryCalculations () {
		if (!this.value.item.expression || this.value.item.expression.length === 0) {
			Vue.set(this.value.item, "expressionCalculationList", [])
			return
		}

		let newCalculationList: string[] = []

		// match all tokens
		let regex = new RegExp("{[^}]+}", "g")
		let matches = this.value.item.expression.matchAll(regex)

		// add every calculation that's actually used in the expression to the calculation list, if it is a valid option
		for (let match of matches) {
			let token = match[0].slice(1, -1)
			if (token !== "value" && this.value.item.expressionCalculationList!.includes(token) &&
					this.calculationOptions.some(calc => calc.field.id === token) && !newCalculationList.includes(token)) {
				newCalculationList.push(token)
			}
		}

		Vue.set(this.value.item, "expressionCalculationList", newCalculationList)
	}

	private removeWronglyGroupedCalculations () {
		// if the current grouped options don't contain the calculation, remove it from the calculation list
		let filteredCalculationList = this.value.item.expressionCalculationList?.filter(calc => this.calculationOptions.some(option => option.field.id === calc)) ?? []
		if (filteredCalculationList.length !== this.value.item.expressionCalculationList?.length) {
			Vue.set(this.value.item, "expressionCalculationList", filteredCalculationList)

			if (!this.value.item.expression) {
				return
			}

			// match all tokens and remove any from an incompatibly grouped calculation
			let matches = this.value.item.expression.match(/(\{.+?\})/g)
			if (matches != null && matches?.length > 0) {
				let updatedExpression = this.value.item.expression
				matches.forEach(match => {
					// remove curly braces for comparison
					let calculationKey = match.slice(1, -1)
					// remove calculation from expression string if it's not a valid option
					if (!filteredCalculationList.includes(calculationKey)) {
						updatedExpression = updatedExpression.replace(match, "REMOVED")
					}
				})

				if (updatedExpression !== this.value.item.expression) {
					Vue.set(this.value.item, "expression", updatedExpression)
				}
			}
		}
	}

	get expressionIsValid (): boolean {
		if (!this.value.item.expression) {
			return false
		}

		let expression = this.value.item.expression

		// side-by-side fields would cause misleading / unintended concatenation (e.g. '{1}{1}' becomes '11')
		if (expression.includes("}{")) {
			return false
		}

		// Match any fields that need to be replaced for the purposes of the validation
		let matches = expression.match(/(\{.+?\})/g)
		if (matches != null && matches.length > 0) {
			matches.forEach(match => {
				// Replace field with value 1
				expression = expression.replace(match, "1")
			})
		}

		// matches any character that is not a digit, mathematical operator, dot, or whitespace as invalid
		if (expression.match(/[^\d\s+\-*/.()%^!]/)) {
			return false
		}

		// evaluate the expression, deem it valid if it computes a result
		try {
			let evaluated = evaluate(expression)
			// ensure there is no division by 0
			return evaluated !== Infinity
		} catch (e) {
			return false
		}
	}

	get calculationsAreUsed (): boolean {
		return !!this.value.item.expressionCalculationList && this.value.item.expressionCalculationList.length > 0
	}

	get expressionIsBlank (): boolean {
		return !this.value.item.expression || this.value.item.expression.trim().length === 0
	}

	get stateIsValid (): boolean {
		return !this.expressionIsBlank && this.calculationsAreUsed && this.expressionIsValid
	}

	async groupByChanged (newValue: string) {
		if (this.tenantEnabledFeatures && this.tenantEnabledFeatures[TenantFeatures.InsightFilterWithCalculations]) {
			if (newValue !== null) {
				this.$emit("filter-calculation-grouped", this.value, newValue)
			} else {
				this.value.item.groupBy = newValue
			}
		} else {
			this.value.item.groupBy = newValue
		}
	}
}
